/**
 * Football Position Recommendation Rules
 * 
 * Defines position recommendations for Football players based on their attributes
 */

import type { PositionRule } from '../positionRecommendations';

/**
 * Football Position Rules
 */
export const FOOTBALL_POSITION_RULES: PositionRule[] = [
  {
    position: 'QB',
    tier: 'premium',
    description: 'Quarterback - Requires throwing attributes',
    requirements: [
      { attribute: 'throwing_power', minValue: 7, required: true },
      { attribute: 'throwing_accuracy', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const throwingPower = attrs.get('throwing_power') || 0;
      const throwingAccuracy = attrs.get('throwing_accuracy') || 0;
      return throwingPower + throwingAccuracy;
    },
  },
  {
    position: 'RB',
    tier: 'standard',
    description: 'Running Back - Speed/agility based',
    requirements: [
      { attribute: 'speed', minValue: 7, required: false },
      { attribute: 'agility', minValue: 7, required: false },
    ],
    scoreFormula: (attrs) => {
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      return speed + agility;
    },
  },
  {
    position: 'WR',
    tier: 'standard',
    description: 'Wide Receiver - Catching and route running',
    requirements: [
      { attribute: 'catching', minValue: 7, required: true },
      { attribute: 'route_running', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const catching = attrs.get('catching') || 0;
      const routeRunning = attrs.get('route_running') || 0;
      return catching + routeRunning;
    },
  },
  {
    position: 'OL',
    tier: 'standard',
    description: 'Offensive Line - Blocking and strength',
    requirements: [
      { attribute: 'blocking', minValue: 7, required: true },
      { attribute: 'strength', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const blocking = attrs.get('blocking') || 0;
      const strength = attrs.get('strength') || 0;
      return blocking + strength;
    },
  },
  {
    position: 'DL',
    tier: 'standard',
    description: 'Defensive Line - Tackling and strength',
    requirements: [
      { attribute: 'tackling', minValue: 7, required: true },
      { attribute: 'strength', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const tackling = attrs.get('tackling') || 0;
      const strength = attrs.get('strength') || 0;
      return tackling + strength;
    },
  },
  {
    position: 'LB',
    tier: 'standard',
    description: 'Linebacker - Tackling and awareness',
    requirements: [
      { attribute: 'tackling', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const tackling = attrs.get('tackling') || 0;
      const awareness = attrs.get('awareness') || 0;
      const speed = attrs.get('speed') || 0;
      const agility = attrs.get('agility') || 0;
      const strength = attrs.get('strength') || 0;
      const blocking = attrs.get('blocking') || 0;
      
      let score = tackling + awareness;
      
      // LB can also play RB (similar physical attributes)
      if (speed >= 6 || agility >= 6) {
        score += (speed + agility) * 0.3;
      }
      
      // LB can occasionally play EDGE (part of DL group)
      if (strength >= 6) {
        score += strength * 0.2;
      }
      
      // LB can occasionally play OL (if strong enough)
      if (strength >= 7 && blocking >= 6) {
        score += blocking * 0.2;
      }
      
      return score;
    },
  },
  {
    position: 'DB',
    tier: 'standard',
    description: 'Defensive Back - Agility and awareness',
    requirements: [
      { attribute: 'agility', minValue: 7, required: true },
      { attribute: 'awareness', minValue: 7, required: true },
    ],
    scoreFormula: (attrs) => {
      const agility = attrs.get('agility') || 0;
      const awareness = attrs.get('awareness') || 0;
      return agility + awareness;
    },
  },
];

